import {
  AlertTriangle,
  BarChart3,
  Calendar,
  ChevronDown,
  ClipboardList,
  Code2,
  FileText,
  Gift,
  Home,
  Inbox,
  LogOut,
  QrCode,
  Store,
  Users,
  X,
  Ticket,
  History,
  Target,
  Wallet,
  RefreshCw,
  Database,
} from "lucide-react";
import React from "react";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";
import { Button } from "../../components/button";
import { useDevMode } from "../../hooks/useDevMode";
import { useAuthSession } from "../auth/shared";

interface SidebarProps {
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  onLogout: () => void;
}

interface SidebarSectionProps {
  title: string;
  children: React.ReactNode;
}

const SidebarSection = ({ title, children }: SidebarSectionProps) => {
  const [isOpen, setIsOpen] = React.useState(true);
  const contentId = React.useId();

  return (
    <section className="pt-3">
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        aria-expanded={isOpen}
        aria-controls={contentId}
        className="flex w-full items-center justify-between rounded-[10px] px-3 py-1 text-left text-xs font-semibold uppercase tracking-[0.3px] text-[#6A7282] transition hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00C49A] dark:text-gray-400 dark:hover:bg-gray-800"
      >
        {title}
        <ChevronDown
          className={`h-4 w-4 transition-transform ${isOpen ? "" : "-rotate-90"}`}
          aria-hidden="true"
        />
      </button>
      {isOpen && (
        <div
          id={contentId}
          className="mt-2 space-y-1"
        >
          {children}
        </div>
      )}
    </section>
  );
};

const Sidebar: React.FC<SidebarProps> = ({
  sidebarOpen,
  setSidebarOpen,
  onLogout,
}: SidebarProps) => {
  const { t } = useTranslation();
  const { user } = useAuthSession();
  const { isUnlocked: isDevModeUnlocked, handleLogoClick } = useDevMode();
  const isSuperAdmin = user?.role === "super_admin";

  return (
    <aside
      className={`fixed z-40 inset-y-0 left-0 w-60 bg-white flex flex-col transition-transform duration-300 lg:static lg:translate-x-0 ${sidebarOpen ? "translate-x-0" : "-translate-x-full"} dark:bg-gray-900`}
    >
      {/* Header Section */}
      <div className="flex items-center h-16 min-h-16 px-4 border-b border-l-0 border-r-0 border-gray-200 dark:border-gray-800">
        <div className="flex items-center space-x-3">
          {/* City Logo SVG - Click 5 times to unlock dev mode */}
          <div
            className="flex items-center justify-center cursor-pointer select-none"
            onClick={handleLogoClick}
            title="Tap 5 times to unlock dev mode"
          >
            <svg
              width="27"
              height="31"
              viewBox="0 0 27 31"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12.5563 26.4705C10.8071 26.4705 9.16734 26.1425 7.63684 25.4866C6.13759 24.8307 4.81012 23.9249 3.65444 22.7692C2.49876 21.5823 1.59296 20.2236 0.937036 18.6931C0.312345 17.1314 0 15.4603 0 13.68C0 11.3061 0.546604 9.16658 1.63981 7.26128C2.76426 5.32474 4.29475 3.77863 6.23129 2.62295C8.19906 1.46727 10.4167 0.889431 12.8842 0.889431C15.1019 0.858196 17.0853 1.24863 18.8344 2.06073C20.5836 2.87282 21.9735 3.99727 23.0042 5.43406L18.1316 9.41646C17.5694 8.57313 16.8354 7.90158 15.9296 7.40183C15.055 6.90208 14.0712 6.6522 12.9779 6.6522C11.6349 6.6522 10.4636 6.98016 9.46406 7.63609C8.46456 8.26078 7.68369 9.10411 7.12147 10.1661C6.59049 11.2281 6.32499 12.3994 6.32499 13.68C6.32499 14.9294 6.6061 16.1006 7.16832 17.1939C7.76178 18.2558 8.55826 19.1148 9.55777 19.7707C10.5885 20.3954 11.7754 20.7077 13.1185 20.7077C14.3367 20.7077 15.3674 20.4422 16.2107 19.9113C17.0541 19.349 17.7881 18.6463 18.4128 17.8029L23.0042 21.7853C21.8486 23.2534 20.4118 24.409 18.6939 25.2524C17.0072 26.0645 14.9613 26.4705 12.5563 26.4705Z"
                fill="#00C49A"
              />
              <path
                d="M13.1746 30.3124L20.7566 23.9874H5.09748L13.1746 30.3124Z"
                fill="#00C49A"
              />
              <path
                d="M20.2866 16.1613C21.0362 16.8797 21.942 17.2389 23.004 17.2389C24.0972 17.2389 25.003 16.8797 25.7214 16.1613C26.4398 15.4429 26.799 14.5527 26.799 13.4907C26.799 12.4912 26.4398 11.6323 25.7214 10.9139C25.003 10.1642 24.0972 9.78943 23.004 9.78943C21.942 9.78943 21.0362 10.1642 20.2866 10.9139C19.5682 11.6323 19.209 12.4912 19.209 13.4907C19.209 14.5215 19.5682 15.4116 20.2866 16.1613Z"
                fill="#FEC057"
              />
            </svg>
          </div>

          {/* Horizontal Divider */}
          <div className="flex items-center justify-center">
            <svg
              width="2"
              height="20"
              viewBox="0 0 2 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="sidebar-divider"
            >
              <rect
                x="1.29883"
                y="0.0999756"
                width="19"
                height="0.999999"
                transform="rotate(90 1.29883 0.0999756)"
                fill="black"
                fillOpacity="0.1"
              />
            </svg>
          </div>

          <span
            className="text-base font-normal text-[#004232] dark:text-white"
            style={{ fontFamily: "Cal Sans" }}
          >
            {user?.organization_name || "City Name"}
          </span>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSidebarOpen(false)}
          className="ml-auto lg:hidden"
        >
          <X className="h-5 w-5" />
        </Button>
      </div>

      <nav className="flex-1 py-4 px-4 space-y-1 overflow-y-auto border-r">
        {/* Main Navigation */}
        <NavLink
          to="/"
          end
          className={({ isActive }) =>
            isActive
              ? "bg-[#EEF9EF] dark:bg-emerald-900/30 text-[#004232] dark:text-emerald-400 w-full flex items-center px-3 py-2 rounded-[10px]"
              : "w-full flex items-center px-3 py-2 rounded-[10px] text-[#4A5565] dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
          }
        >
          <Home className="mr-3 h-5 w-5" />
          <span className="text-base font-normal leading-6">{t("navigation.dashboard")}</span>
        </NavLink>

        <NavLink
          to="/heat-map"
          end
          className={({ isActive }) =>
            isActive
              ? "bg-[#EEF9EF] dark:bg-emerald-900/30 text-[#004232] dark:text-emerald-400 w-full flex items-center px-3 py-2 rounded-[10px]"
              : "w-full flex items-center px-3 py-2 rounded-[10px] text-[#4A5565] dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
          }
        >
          <BarChart3 className="mr-3 h-5 w-5" />
          <span className="text-base font-normal leading-6">
            {t("navigation.analytic_heat_map")}
          </span>
        </NavLink>

        {isSuperAdmin && (
          <NavLink
            to="/campaign"
            className={({ isActive }) =>
              isActive
                ? "bg-[#EEF9EF] dark:bg-emerald-900/30 text-[#004232] dark:text-emerald-400 w-full flex items-center px-3 py-2 rounded-[10px]"
                : "w-full flex items-center px-3 py-2 rounded-[10px] text-[#4A5565] dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
            }
          >
            <Target className="mr-3 h-5 w-5" />
            <span className="text-base font-normal leading-6">Campaign</span>
          </NavLink>
        )}

        <NavLink
          to="/event"
          className={({ isActive }) =>
            isActive
              ? "bg-[#EEF9EF] dark:bg-emerald-900/30 text-[#004232] dark:text-emerald-400 w-full flex items-center px-3 py-2 rounded-[10px]"
              : "w-full flex items-center px-3 py-2 rounded-[10px] text-[#4A5565] dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
          }
        >
          <Calendar className="mr-3 h-5 w-5" />
          <span className="text-base font-normal leading-6">{t("navigation.events")}</span>
        </NavLink>

        <NavLink
          to="/survey"
          className={({ isActive }) =>
            isActive
              ? "bg-[#EEF9EF] dark:bg-emerald-900/30 text-[#004232] dark:text-emerald-400 w-full flex items-center px-3 py-2 rounded-[10px]"
              : "w-full flex items-center px-3 py-2 rounded-[10px] text-[#4A5565] dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
          }
        >
          <ClipboardList className="mr-3 h-5 w-5" />
          <span className="text-base font-normal leading-6">{t("navigation.surveys")}</span>
        </NavLink>

        <NavLink
          to="/e-gift-card"
          className={({ isActive }) =>
            isActive
              ? "bg-[#EEF9EF] dark:bg-emerald-900/30 text-[#004232] dark:text-emerald-400 w-full flex items-center px-3 py-2 rounded-[10px]"
              : "w-full flex items-center px-3 py-2 rounded-[10px] text-[#4A5565] dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
          }
        >
          <Gift className="mr-3 h-5 w-5" />
          <span className="text-base font-normal leading-6">{t("navigation.rewards")}</span>
        </NavLink>

        <NavLink
          to="/notification"
          className={({ isActive }) =>
            isActive
              ? "bg-[#EEF9EF] dark:bg-emerald-900/30 text-[#004232] dark:text-emerald-400 w-full flex items-center px-3 py-2 rounded-[10px]"
              : "w-full flex items-center px-3 py-2 rounded-[10px] text-[#4A5565] dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
          }
        >
          <AlertTriangle className="mr-3 h-5 w-5" />
          <span className="text-base font-normal leading-6">{t("navigation.alerts")}</span>
        </NavLink>

        <NavLink
          to="/news"
          className={({ isActive }) =>
            isActive
              ? "bg-[#EEF9EF] dark:bg-emerald-900/30 text-[#004232] dark:text-emerald-400 w-full flex items-center px-3 py-2 rounded-[10px]"
              : "w-full flex items-center px-3 py-2 rounded-[10px] text-[#4A5565] dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
          }
        >
          <FileText className="mr-3 h-5 w-5" />
          <span className="text-base font-normal leading-6">{t("navigation.news")}</span>
        </NavLink>

        <NavLink
          to="/qr-scanner-tutorial"
          className={({ isActive }) =>
            isActive
              ? "bg-[#EEF9EF] dark:bg-emerald-900/30 text-[#004232] dark:text-emerald-400 w-full flex items-center px-3 py-2 rounded-[10px]"
              : "w-full flex items-center px-3 py-2 rounded-[10px] text-[#4A5565] dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
          }
        >
          <QrCode className="mr-3 h-5 w-5" />
          <span className="text-base font-normal leading-6">
            {t("navigation.qr-scanner-tutorial")}
          </span>
        </NavLink>

        {/* Dev Playground - Only show when unlocked (tap logo 5 times) */}
        {isSuperAdmin && isDevModeUnlocked && (
          <NavLink
            to="/dev-playground"
            className={({ isActive }) =>
              isActive
                ? "bg-[#EEF9EF] dark:bg-emerald-900/30 text-[#004232] dark:text-emerald-400 w-full flex items-center px-3 py-2 rounded-[10px]"
                : "w-full flex items-center px-3 py-2 rounded-[10px] text-[#4A5565] dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
            }
          >
            <Code2 className="mr-3 h-5 w-5" />
            <span className="text-base font-normal leading-6">Dev Playground</span>
          </NavLink>
        )}

        {/* Separator Line */}
        <div className="w-full h-px bg-black bg-opacity-10 dark:bg-white dark:bg-opacity-10 my-4"></div>

        <SidebarSection title={t("navigation.user_data")}>
          <NavLink
            to="/users"
            className={({ isActive }) =>
              isActive
                ? "bg-[#EEF9EF] dark:bg-emerald-900/30 text-[#004232] dark:text-emerald-400 w-full flex items-center px-3 py-2 rounded-[10px]"
                : "w-full flex items-center px-3 py-2 rounded-[10px] text-[#4A5565] dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
            }
          >
            <Users className="mr-3 h-5 w-5" />
            <span className="text-base font-normal leading-6">{t("navigation.users")}</span>
          </NavLink>

          {isSuperAdmin && (
            <NavLink
              to="/merchant-management"
              className={({ isActive }) =>
                isActive
                  ? "bg-[#EEF9EF] dark:bg-emerald-900/30 text-[#004232] dark:text-emerald-400 w-full flex items-center px-3 py-2 rounded-[10px]"
                  : "w-full flex items-center px-3 py-2 rounded-[10px] text-[#4A5565] dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
              }
            >
              <Store className="mr-3 h-5 w-5" />
              <span className="text-base font-normal leading-6">{t("navigation.merchants")}</span>
            </NavLink>
          )}

          <NavLink
            to="/attendance"
            className={({ isActive }) =>
              isActive
                ? "bg-[#EEF9EF] dark:bg-emerald-900/30 text-[#004232] dark:text-emerald-400 w-full flex items-center px-3 py-2 rounded-[10px]"
                : "w-full flex items-center px-3 py-2 rounded-[10px] text-[#4A5565] dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
            }
          >
            <Users className="mr-3 h-5 w-5" />
            <span className="text-base font-normal leading-6">{t("navigation.attendance")}</span>
          </NavLink>

          <NavLink
            to="/reward-history"
            className={({ isActive }) =>
              isActive
                ? "bg-[#EEF9EF] dark:bg-emerald-900/30 text-[#004232] dark:text-emerald-400 w-full flex items-center px-3 py-2 rounded-[10px]"
                : "w-full flex items-center px-3 py-2 rounded-[10px] text-[#4A5565] dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
            }
          >
            <Gift className="mr-3 h-5 w-5" />
            <span className="text-base font-normal leading-6">
              {t("navigation.reward_history")}
            </span>
          </NavLink>

          {/* <NavLink
                        to="/support"
                        className={({ isActive }) =>
                        (isActive
                            ? "bg-[#EEF9EF] dark:bg-emerald-900/30 text-[#004232] dark:text-emerald-400 w-full flex items-center px-3 py-2 rounded-[10px]"
                            : "w-full flex items-center px-3 py-2 rounded-[10px] text-[#4A5565] dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800")
                        }
                    >
                        <MessageCircle className="mr-3 h-5 w-5" />
                        <span className="text-base font-normal leading-6">{t('navigation.support')}</span>
                    </NavLink> */}
        </SidebarSection>

        <NavLink
          to="/reports"
          className={({ isActive }) =>
            isActive
              ? "bg-[#EEF9EF] dark:bg-emerald-900/30 text-[#004232] dark:text-emerald-400 w-full flex items-center px-3 py-2 rounded-[10px]"
              : "w-full flex items-center px-3 py-2 rounded-[10px] text-[#4A5565] dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
          }
        >
          <BarChart3 className="mr-3 h-5 w-5" />
          <span className="text-base font-normal leading-6">{t("navigation.reports")}</span>
        </NavLink>

        {isSuperAdmin && (
          <SidebarSection title={t("navigation.catalog")}>
            <NavLink
              to="/catalog/categories"
              className={({ isActive }) =>
                isActive
                  ? "bg-[#EEF9EF] dark:bg-emerald-900/30 text-[#004232] dark:text-emerald-400 w-full flex items-center px-3 py-2 rounded-[10px]"
                  : "w-full flex items-center px-3 py-2 rounded-[10px] text-[#4A5565] dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
              }
            >
              <Database className="mr-3 h-5 w-5" />
              <span className="text-base font-normal leading-6">
                {t("navigation.categories")}
              </span>
            </NavLink>
            <NavLink
              to="/catalog/merchant-categories"
              className={({ isActive }) =>
                isActive
                  ? "bg-[#EEF9EF] dark:bg-emerald-900/30 text-[#004232] dark:text-emerald-400 w-full flex items-center px-3 py-2 rounded-[10px]"
                  : "w-full flex items-center px-3 py-2 rounded-[10px] text-[#4A5565] dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
              }
            >
              <Database className="mr-3 h-5 w-5" />
              <span className="text-base font-normal leading-6">
                {t("navigation.merchant_categories")}
              </span>
            </NavLink>
            <NavLink
              to="/catalog/merchant-brands"
              className={({ isActive }) =>
                isActive
                  ? "bg-[#EEF9EF] dark:bg-emerald-900/30 text-[#004232] dark:text-emerald-400 w-full flex items-center px-3 py-2 rounded-[10px]"
                  : "w-full flex items-center px-3 py-2 rounded-[10px] text-[#4A5565] dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
              }
            >
              <Store className="mr-3 h-5 w-5" />
              <span className="text-base font-normal leading-6">
                {t("navigation.merchant_brands")}
              </span>
            </NavLink>
          </SidebarSection>
        )}

        {isSuperAdmin && (
          <SidebarSection title={t("navigation.administrator")}>
            <NavLink
              to="/organizations"
              className={({ isActive }) =>
                isActive
                  ? "bg-[#EEF9EF] dark:bg-emerald-900/30 text-[#004232] dark:text-emerald-400 w-full flex items-center px-3 py-2 rounded-[10px]"
                  : "w-full flex items-center px-3 py-2 rounded-[10px] text-[#4A5565] dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
              }
            >
              <Users className="mr-3 h-5 w-5" />
              <span className="text-base font-normal leading-6">{t("navigation.organizations")}</span>
            </NavLink>
            <NavLink
              to="/wallet-transaction"
              className={({ isActive }) =>
                isActive
                  ? "bg-[#EEF9EF] dark:bg-emerald-900/30 text-[#004232] dark:text-emerald-400 w-full flex items-center px-3 py-2 rounded-[10px]"
                  : "w-full flex items-center px-3 py-2 rounded-[10px] text-[#4A5565] dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
              }
            >
              <Wallet className="mr-3 h-5 w-5" />
              <span className="text-base font-normal leading-6">
                {t("navigation.wallet_transaction")}
              </span>
            </NavLink>
            <NavLink
              to="/missing-rewards"
              className={({ isActive }) =>
                isActive
                  ? "bg-[#EEF9EF] dark:bg-emerald-900/30 text-[#004232] dark:text-emerald-400 w-full flex items-center px-3 py-2 rounded-[10px]"
                  : "w-full flex items-center px-3 py-2 rounded-[10px] text-[#4A5565] dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
              }
            >
              <RefreshCw className="mr-3 h-5 w-5" />
              <span className="text-base font-normal leading-6">
                {t("navigation.missing_rewards")}
              </span>
            </NavLink>
            <NavLink
              to="/runtime-configuration"
              className={({ isActive }) =>
                isActive
                  ? "bg-[#EEF9EF] dark:bg-emerald-900/30 text-[#004232] dark:text-emerald-400 w-full flex items-center px-3 py-2 rounded-[10px]"
                  : "w-full flex items-center px-3 py-2 rounded-[10px] text-[#4A5565] dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
              }
            >
              <Database className="mr-3 h-5 w-5" />
              <span className="text-base font-normal leading-6">
                {t("navigation.runtime_configuration")}
              </span>
            </NavLink>
          </SidebarSection>
        )}

        {isSuperAdmin && (
          <SidebarSection title={t("navigation.background_job")}>
            <NavLink
              to="/sync-points"
              className={({ isActive }) =>
                isActive
                  ? "bg-[#EEF9EF] dark:bg-emerald-900/30 text-[#004232] dark:text-emerald-400 w-full flex items-center px-3 py-2 rounded-[10px]"
                  : "w-full flex items-center px-3 py-2 rounded-[10px] text-[#4A5565] dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
              }
            >
              <RefreshCw className="mr-3 h-5 w-5" />
              <span className="text-base font-normal leading-6">{t("navigation.sync_points")}</span>
            </NavLink>
            <NavLink
              to="/outbox-notifications"
              className={({ isActive }) =>
                isActive
                  ? "bg-[#EEF9EF] dark:bg-emerald-900/30 text-[#004232] dark:text-emerald-400 w-full flex items-center px-3 py-2 rounded-[10px]"
                  : "w-full flex items-center px-3 py-2 rounded-[10px] text-[#4A5565] dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
              }
            >
              <Inbox className="mr-3 h-5 w-5" />
              <span className="text-base font-normal leading-6">
                {t("navigation.outbox_notifications")}
              </span>
            </NavLink>
          </SidebarSection>
        )}
      </nav>

      {/* Separator Line */}
      {/* <div className="border-t border-[#E6E6E6] dark:border-[#212936]"></div> */}

      {/* Logout Section */}
      <div className="p-4 border-r border-[#e2e8f0] dark:border-gray-700">
        <Button
          variant="ghost"
          className="w-full justify-start text-[#4A5565] hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800 px-3 py-2 rounded-[10px]"
          onClick={onLogout}
        >
          <LogOut className="mr-3 h-5 w-5" />
          <span className="text-base font-normal leading-6">{t("common.logout")}</span>
        </Button>
      </div>
    </aside>
  );
};

export default Sidebar;
